import { Injectable, NgZone } from '@angular/core';

import { of, Subject } from 'rxjs';

import { App } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { ActionPerformed, PushNotifications } from '@capacitor/push-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';
import { NavController, Platform } from '@ionic/angular';
// import { Camera, CameraOptions } from '@ionic-native/camera/ngx';

import { Keychain } from '@ionic-native/keychain/ngx';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CordovaService {
  public deviceToken: string = null;

  public keyboardWillShow$ = new Subject<number>();
  public keyboardWillHide$ = new Subject();
  public pushNotifData$ = new Subject<any>();

  public appBuildNumber = 0;

  constructor(
    private platform: Platform,
    private keychain: Keychain,
    // private camera: Camera,
    // private appVersion: AppVersion,
    private ngZone: NgZone,
    private navController: NavController
  ) {
    // init cordova plugins when the platform is ready
    this.platform.ready().then(() => {
      if (environment.isNativeApp) {
        this.onDeviceReady();
      }
    });
  }

  isMobileWeb() {
    return this.platform.is('mobileweb');
  }

  isNativeIOS() {
    return environment.isNativeApp && this.platform.is('ios');
  }

  isNativeAndroid() {
    return environment.isNativeApp && this.platform.is('android');
  }

  hasEmojiKeyboard() {
    return this.platform.is('ios') || this.platform.is('android');
  }

  statusBarBlackText() {
    if (this.isNativeIOS()) {
      StatusBar.setStyle({ style: Style.Light });
    }
  }

  statusBarWhiteText() {
    if (this.isNativeIOS()) {
      StatusBar.setStyle({ style: Style.Dark });
    }
  }

  getFromKeychain(key, message?: string) {
    if (this.isNativeIOS()) {
      return this.keychain
        .get(key, message)
        .catch(error => console.warn('Error getting password from keychain:', error));
    } else {
      return of('').toPromise();
    }
  }

  // Combining info from  https://stackoverflow.com/a/11672200
  // and the code at https://github.com/ionic-team/cordova-plugin-ios-keychain/blob/master/src/ios/A0SimpleKeychain.m ,
  // it seems there is no need to namespace the keys,
  // as the keychain plugin already sets kSecAttrService to our bundle indentifier)
  setToKeychain(key, value, requirePresence) {
    if (this.isNativeIOS()) {
      this.keychain.set(key, value, requirePresence).catch(error => console.warn('Error setting to keychain:', error));
    }
  }

  async getPictureFromLibraryAsBlob(): Promise<Blob> {
    return this.pictureAsBlob(/* this.camera.PictureSourceType.PHOTOLIBRARY */);
  }

  async takePhotoWithCameraAsBlob(): Promise<Blob> {
    return this.pictureAsBlob(/* this.camera.PictureSourceType.CAMERA */);
  }

  private async pictureAsBlob(): Promise<Blob> {
    return await Promise.resolve(null);
    // const options: CameraOptions = {
    //   sourceType,
    //   destinationType: this.camera.DestinationType.DATA_URL,
    //   encodingType: this.camera.EncodingType.JPEG,
    //   correctOrientation: true,
    // };
    // const imageData = await this.camera.getPicture(options);
    // const imageURI = 'data:image/jpeg;base64,' + imageData;
    // return (await fetch(imageURI)).blob();
  }

  private onDeviceReady() {
    // hook keyboard show/hide events
    Keyboard.addListener('keyboardWillShow', info =>
      this.ngZone.run(() => {
        this.keyboardWillShow$.next(info.keyboardHeight);
      })
    );
    Keyboard.addListener('keyboardWillHide', () =>
      this.ngZone.run(() => {
        this.keyboardWillHide$.next();
      })
    );

    // show the status bar (was hidden during the splash screen by UIStatusBarHidden = true in config.xml)
    StatusBar.show();

    // Retrieve build version
    App.getInfo().then(info => (this.appBuildNumber = Number(info.build)));

    // Register for push notifications.
    this.initPushNotifications();

    // Register deep links (to open from backoffice)
    this.initDeepLinks();
  }

  private async initPushNotifications() {
    await PushNotifications.addListener('registration', token => {
      this.deviceToken = token.value;
    });
    await PushNotifications.addListener('registrationError', err => {
      console.error('Registration error: ', err.error);
    });
    await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      // Called when tapping on a notification
      this.pushNotifData$.next(action.notification.data);
    });
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') {
      console.warn('User denied permissions!');
    } else {
      await PushNotifications.register();
    }
  }

  private initDeepLinks() {
    App.addListener('appUrlOpen', ({ url }) => {
      this.ngZone.run(() => {
        const hostAndPrefix = `alcmeon.com/m${environment.isNativeApp ? '/mobile' : ''}`; // hardcoded in AndroidManifest and apple-app-site-association; might as well hcode it here
        const route = url.split(hostAndPrefix).pop();
        if (route) {
          this.navController.navigateRoot(route);
        }
      });
    });
  }
}
